import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2, Package, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

const gbp = (v) => `£${(Number(v) || 0).toFixed(2)}`;

const STATUS_STYLES = {
  paid: "bg-emerald-100 text-emerald-800",
  unpaid: "bg-amber-100 text-amber-800",
  refunded: "bg-slate-100 text-slate-700",
};

const SUPPLIER_STYLES = {
  placed: "bg-emerald-100 text-emerald-800",
  placing: "bg-amber-100 text-amber-800",
  failed: "bg-red-100 text-red-800",
  null: "bg-slate-100 text-slate-500",
};

export default function AdminInkOrders() {
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(new Set());

  const load = async () => {
    setRefreshing(true);
    setError("");
    try {
      const r = await api.get("/admin/ink-orders");
      setOrders(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = (id) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA] text-[var(--brand-ink)]" data-testid="admin-ink-orders">
      <main className="mx-auto max-w-6xl px-4 py-10">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-[var(--brand-body)] hover:text-[var(--brand-ink)]" data-testid="ink-orders-back">
              <ArrowLeft size={14} /> Back to admin
            </Link>
            <span className="text-black/20">·</span>
            <h1 className="font-display text-2xl font-black">Ink shop orders</h1>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={refreshing}
            className="inline-flex items-center gap-1.5 rounded-md border border-black/15 px-3 py-2 font-display text-xs font-semibold hover:border-[#0A0F17] disabled:opacity-50"
            data-testid="ink-orders-refresh"
          >
            {refreshing ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw size={12} />} Refresh
          </button>
        </div>

        {error ? (
          <div className="mb-4 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="ink-orders-error">
            {error}
          </div>
        ) : null}

        {orders === null ? (
          <div className="rounded-md border border-black/10 bg-white p-8 text-center">
            <Loader2 className="mx-auto h-5 w-5 animate-spin" />
          </div>
        ) : orders.length === 0 ? (
          <div className="rounded-md border border-dashed border-black/20 bg-white p-10 text-center" data-testid="ink-orders-empty">
            <Package className="mx-auto h-6 w-6 text-[var(--brand-body)]" />
            <p className="mt-2 text-sm text-[var(--brand-body)]">No orders yet — as soon as your first customer checks out, they&rsquo;ll appear here.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-black/10 bg-white" data-testid="ink-orders-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F7FA] text-left text-[10px] uppercase tracking-wide text-[var(--brand-body)]">
                <tr>
                  <th className="px-4 py-2">Date</th>
                  <th className="px-4 py-2">Customer</th>
                  <th className="px-4 py-2">Total</th>
                  <th className="px-4 py-2">Payment</th>
                  <th className="px-4 py-2">Inkthreadable</th>
                  <th className="px-4 py-2">Shipping</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => {
                  const supStatus = o.supplier_order?.status ?? null;
                  const isOpen = expanded.has(o.id);
                  return (
                    <React.Fragment key={o.id}>
                      <tr className="border-t border-black/5" data-testid={`ink-order-row-${o.id}`}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {new Date(o.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold">{o.customer_name || "—"}</div>
                          <div className="text-xs text-[var(--brand-body)]">{o.customer_email || "—"}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap font-semibold">{gbp(o.amount_gbp)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block rounded-sm px-2 py-0.5 text-[10px] font-semibold ${STATUS_STYLES[o.payment_status] || STATUS_STYLES.unpaid}`}>
                            {o.payment_status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-block rounded-sm px-2 py-0.5 text-[10px] font-semibold ${SUPPLIER_STYLES[supStatus] || SUPPLIER_STYLES.null}`}>
                            {supStatus || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-[var(--brand-body)]">
                          {o.shipping_label || "—"}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button type="button" onClick={() => toggle(o.id)} className="text-xs underline" data-testid={`ink-order-toggle-${o.id}`}>
                            {isOpen ? "Hide" : "Details"}
                          </button>
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr className="border-t border-black/5 bg-[#F9FAFB]">
                          <td colSpan={7} className="px-4 py-4 text-xs">
                            <div className="grid gap-4 lg:grid-cols-2">
                              <div>
                                <p className="label-kicker text-[var(--brand-body)]">Line items</p>
                                <ul className="mt-1 space-y-1">
                                  {(o.line_summary || []).map((l, i) => (
                                    <li key={i}>
                                      <span className="font-mono text-[10px] text-[var(--brand-body)]">{l.pn}</span>
                                      {" · "}{l.title} — {l.variant_label} × {l.quantity} · £{(l.unit_price_gbp * l.quantity).toFixed(2)}
                                    </li>
                                  ))}
                                </ul>
                                <p className="mt-3 text-[var(--brand-body)]">
                                  Subtotal £{Number(o.subtotal_gbp || 0).toFixed(2)} + Shipping £{Number(o.shipping_gbp || 0).toFixed(2)} = <strong>£{Number(o.amount_gbp || 0).toFixed(2)}</strong>
                                </p>
                                <p className="mt-1 text-[var(--brand-body)]">Weight {o.weight_grams}g · {o.country}</p>
                              </div>
                              <div>
                                {o.shipping_address ? (
                                  <>
                                    <p className="label-kicker text-[var(--brand-body)]">Ship to</p>
                                    <p className="mt-1 whitespace-pre-line">
                                      {o.shipping_address.name}{"\n"}
                                      {o.shipping_address.line1}{o.shipping_address.line2 ? `\n${o.shipping_address.line2}` : ""}{"\n"}
                                      {o.shipping_address.city}{o.shipping_address.postcode ? `, ${o.shipping_address.postcode}` : ""}{"\n"}
                                      {o.shipping_address.country}
                                    </p>
                                  </>
                                ) : (
                                  <p className="text-[var(--brand-body)]">Address not yet received (payment pending).</p>
                                )}
                                {o.supplier_order?.error ? (
                                  <p className="mt-3 rounded-sm border border-red-200 bg-red-50 p-2 text-red-700">
                                    Inkthreadable error: {o.supplier_order.error}
                                  </p>
                                ) : null}
                              </div>
                            </div>
                            <p className="mt-3 font-mono text-[10px] text-[var(--brand-body)]">
                              Stripe session: {o.session_id} · Ref: {o.id}
                            </p>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
